import { Button, Modal, Table } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Cookies } from "react-cookie";
import {
  ApiError,
  fetchWithoutResponse,
  fetchWithResponse,
} from "../../../utils/EasyFetch";
import { loading, notLoading } from "../../../features/loading/loadingSlice";
import { LoginResponse } from "../../../features/login/types";
import {
  basicCredential,
  Credential,
  CredentialBeingCreated,
  credentialToRow,
} from "../../../types/credentials";
import {
  selectAccessAuthorization,
  selectRefreshAuthorization,
  updateAccessToken,
} from "../../../features/login/loginSlice";
import CreateCredential from "./CreateCredential";
import EditCredential from "./EditCredential";

const CredentialsLayout = () => {
  const accessToken = useAppSelector(selectAccessAuthorization);
  const refreshToken = useAppSelector(selectRefreshAuthorization);
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalForEdit, setIsModalForEdit] = useState(false);
  const [editCredential, setEditCredential] = useState<Credential | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [createCredential, setCreateCredential] =
    useState<CredentialBeingCreated>({
      userName: "username",
      password: "password",
      roles: [],
    });

  useEffect(() => {
    const cookies = new Cookies();
    fetchWithResponse<Credential[]>(
      "GET",
      "/credentials",
      undefined,
      "Bearer " + accessToken
    )
      .then((c: Credential[]) => {
        setCredentials(c);
        dispatch(notLoading());
      })
      .catch((err: ApiError) => {
        if (err.statusCode === 401) {
          fetchWithResponse<LoginResponse>(
            "POST",
            "/auth/refresh",
            undefined,
            "Bearer " + refreshToken
          )
            .then((a: LoginResponse) => {
              cookies.set("accessToken", a.accessToken, {
                path: "/",
                sameSite: true,
              });
              dispatch(updateAccessToken(a.accessToken));
              dispatch(notLoading);
            })
            .catch((err: any) => {
              dispatch(notLoading());
            });
        } else {
          dispatch(notLoading());
        }
      });
  }, [accessToken, dispatch, refreshToken]);

  useEffect(() => {
    const cookies = new Cookies();
    fetchWithResponse<string[]>(
      "GET",
      "/roles",
      undefined,
      "Bearer " + accessToken
    )
      .then((r: string[]) => {
        setRoles(r);
        dispatch(notLoading());
      })
      .catch((err: ApiError) => {
        if (err.statusCode === 401) {
          fetchWithResponse<LoginResponse>(
            "POST",
            "/auth/refresh",
            undefined,
            "Bearer " + refreshToken
          )
            .then((a: LoginResponse) => {
              cookies.set("accessToken", a.accessToken, {
                path: "/",
                sameSite: true,
              });
              dispatch(updateAccessToken(a.accessToken));
              dispatch(notLoading);
            })
            .catch((err: any) => {
              dispatch(notLoading());
            });
        } else {
          dispatch(notLoading());
        }
      });
  }, [accessToken, dispatch, refreshToken]);

  const showEditModal = (text: string, record: any) => {
    dispatch(loading());
    fetchWithResponse<Credential>(
      "GET",
      "/credentials/" + record.key,
      undefined,
      "Bearer " + accessToken
    )
      .then((credential: Credential) => {
        dispatch(notLoading());
        setEditCredential(credential);
        setIsModalOpen(true);
        setIsModalForEdit(true);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        console.error(err);
      });
  };

  const handleCreateCredential = () => {
    dispatch(loading());
    fetchWithoutResponse(
      "POST",
      "/credentials",
      createCredential,
      "Bearer " + accessToken
    )
      .then(() => {
        const newCredential: Credential = {
          ...createCredential,
          key: createCredential.userName,
        };

        setCredentials((prevCredentials) => [
          ...prevCredentials,
          newCredential,
        ]);
        setIsModalOpen(false);
        setCreateCredential(basicCredential);
        dispatch(notLoading());
      })
      .catch((err: ApiError) => {
        console.error(err);
        dispatch(notLoading());
      });
  };

  const handleCreateCancel = () => {
    setIsModalOpen(false);
  };

  const handleEditCredential = () => {
    if (!editCredential) return;
    const { userName, ...updatedCredential } = editCredential;

    dispatch(loading());
    fetchWithoutResponse(
      "PATCH",
      `/credentials/${userName}`,
      updatedCredential,
      `Bearer ${accessToken}`
    )
      .then(() => {
        setCredentials((prev) =>
          prev.map((c) =>
            c.userName === userName ? { ...c, ...updatedCredential } : c
          )
        );
        setIsModalOpen(false);
        setEditCredential(null);
        dispatch(notLoading());
      })
      .catch((err: ApiError) => {
        console.error("Error editing credential:", err);
        dispatch(notLoading());
      });
  };

  const handleEditCancel = () => {
    setIsModalOpen(false);
  };

  const handleDeleteCredential = (credentialId: string) => {
    Modal.confirm({
      title: "Delete Credential",
      content: "This can't be undone",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      maskClosable: true,
      onOk: () => {
        dispatch(loading());
        fetchWithoutResponse(
          "DELETE",
          "/credentials/" + credentialId,
          undefined,
          "Bearer " + accessToken
        )
          .then(() => {
            dispatch(notLoading());
          })
          .catch((err: ApiError) => {
            console.error(err);
            dispatch(notLoading());
          });
      },
    });
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
    },
    {
      title: "Edit",
      key: "edit",
      render: (text: string, record: any) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showEditModal(text, record)}
        />
      ),
    },
    {
      title: "Delete",
      key: "delete",
      render: (text: string, record: any) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteCredential(record.key)}
        />
      ),
    },
  ];

  return (
    <div>
      <Table
        dataSource={credentials.map((c) => credentialToRow(c))}
        columns={columns}
        pagination={false}
        bordered={true}
        showHeader={false}
        style={{ margin: "1%" }}
      />
      <Modal
        title={isModalForEdit ? "Edit Credential" : "Create Credential"}
        open={isModalOpen}
        onOk={handleCreateCredential}
        onCancel={() =>
          isModalForEdit ? handleEditCancel() : handleCreateCancel()
        }
        footer={[
          <Button key="back" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={
              isModalForEdit ? handleEditCredential : handleCreateCredential
            }
          >
            {isModalForEdit ? "Save Changes" : "Create Credential"}
          </Button>,
        ]}
      >
        {isModalForEdit && editCredential ? (
          <EditCredential
            credential={editCredential}
            setCredential={setEditCredential}
            roles={roles}
          />
        ) : (
          <CreateCredential
            roles={roles}
            credential={createCredential}
            setCredential={setCreateCredential}
          />
        )}
      </Modal>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          setIsModalForEdit(false);
          setIsModalOpen(true);
        }}
      >
        Create New Credential
      </Button>
    </div>
  );
};

export default CredentialsLayout;
